import { useState, useRef } from "react";
import { mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus,
  Upload,
  Search,
  Trash2,
  Edit,
  Trophy,
  Calendar,
  Users,
  FolderOpen,
  Wand2,
  Loader2,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/store/useStore";
import { cn } from "@/lib/utils";
import { extractDominantColor } from "@/lib/extractColor";
import { MediaPicker } from "@/components/MediaPicker";

const SPORTS = ["Football", "Basketball", "Tennis", "Cricket", "Rugby", "Hockey", "Volleyball", "Baseball", "Gaelic Football", "Hurling", "Camogie"];

export function LeaguesPage() {
  const { leagues, teams, addLeague, updateLeague, deleteLeague, generateFixtures, reloadMatches } = useStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLeague, setEditingLeague] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [generatingFixtures, setGeneratingFixtures] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    sport: "",
    logo: "",
    primaryColor: "#1e3a5f",
    secondaryColor: "#0ea5e9",
    teamIds: [],
  });
  const fileInputRef = useRef(null);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);

  // --- Bulk fixture generation ---
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, current: "" });
  const [bulkResults, setBulkResults] = useState([]); // { leagueId, name, ok, count, error }

  const eligibleLeagues = leagues.filter((l) => (l.teamIds?.length || 0) >= 2);

  const openBulkDialog = () => {
    setBulkSelectedIds(eligibleLeagues.map((l) => l.id)); // default: all selected
    setBulkResults([]);
    setBulkProgress({ done: 0, total: 0, current: "" });
    setIsBulkOpen(true);
  };

  const toggleBulkLeague = (leagueId) => {
    setBulkSelectedIds((prev) =>
      prev.includes(leagueId) ? prev.filter((id) => id !== leagueId) : [...prev, leagueId]
    );
  };

  const toggleBulkAll = () => {
    setBulkSelectedIds((prev) =>
      prev.length === eligibleLeagues.length ? [] : eligibleLeagues.map((l) => l.id)
    );
  };

  const runBulkGenerate = async () => {
    if (bulkSelectedIds.length === 0) {
      toast.error("Select at least one league");
      return;
    }
    setBulkRunning(true);
    setBulkResults([]);
    const targets = eligibleLeagues.filter((l) => bulkSelectedIds.includes(l.id));
    setBulkProgress({ done: 0, total: targets.length, current: "" });

    const results = [];
    for (let i = 0; i < targets.length; i++) {
      const league = targets[i];
      setBulkProgress({ done: i, total: targets.length, current: league.name });
      try {
        const count = await generateFixtures(league.id);
        results.push({ leagueId: league.id, name: league.name, ok: true, count });
      } catch (err) {
        results.push({
          leagueId: league.id,
          name: league.name,
          ok: false,
          error: err?.response?.data?.detail || err?.message || "Failed",
        });
      }
    }

    setBulkProgress({ done: targets.length, total: targets.length, current: "" });
    setBulkResults(results);
    // Re-sync matches from server so the store doesn't keep stale auto-gen rows
    // that the server replaced during the bulk run.
    try {
      await reloadMatches();
    } catch {
      // non-fatal
    }
    setBulkRunning(false);

    const totalFixtures = results.reduce((sum, r) => sum + (r.ok ? r.count : 0), 0);
    const failed = results.filter((r) => !r.ok).length;
    if (failed === 0) {
      toast.success(
        `Generated ${totalFixtures} fixtures across ${results.length} leagues`,
        { description: "Existing auto-generated fixtures were replaced." }
      );
    } else {
      toast.warning(
        `Generated ${totalFixtures} fixtures (${failed} league${failed === 1 ? "" : "s"} failed)`,
        { description: "Check the dialog for details." }
      );
    }
  };
  // --- end bulk fixture generation ---

  const handleMediaPick = async (item) => {
    const newLogoUrl = item.url;
    setFormData((prev) => ({ ...prev, logo: newLogoUrl }));
    const userHasCustomized =
      editingLeague &&
      formData.primaryColor !== "#1e3a5f" &&
      formData.primaryColor !== editingLeague.primaryColor;
    if (!userHasCustomized) {
      try {
        const color = await extractDominantColor(mediaUrl(newLogoUrl));
        setFormData((prev) => ({ ...prev, primaryColor: color }));
      } catch {
        // ignore
      }
    }
  };

  const filteredLeagues = leagues.filter(
    (league) =>
      league.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      league.sport.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = reader.result;
        const userHasCustomized =
          editingLeague &&
          formData.primaryColor !== "#1e3a5f" &&
          formData.primaryColor !== editingLeague.primaryColor;

        setFormData((prev) => ({ ...prev, logo: dataUrl }));

        if (!userHasCustomized) {
          try {
            const color = await extractDominantColor(dataUrl);
            setFormData((prev) => ({ ...prev, logo: dataUrl, primaryColor: color }));
            toast.success("Auto-picked color from logo", {
              description: `${color.toUpperCase()} — feel free to tweak below`,
            });
          } catch {
            // Silent fail; user can still pick manually
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.sport) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      if (editingLeague) {
        await updateLeague(editingLeague.id, formData);
        toast.success("League updated successfully");
      } else {
        await addLeague({ ...formData, teamIds: formData.teamIds || [] });
        toast.success("League added successfully");
      }
      setFormData({ name: "", sport: "", logo: "", primaryColor: "#1e3a5f", secondaryColor: "#0ea5e9", teamIds: [] });
      setEditingLeague(null);
      setIsDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save league");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (league) => {
    setEditingLeague(league);
    setFormData({
      name: league.name,
      sport: league.sport,
      logo: league.logo,
      primaryColor: league.primaryColor || "#1e3a5f",
      secondaryColor: league.secondaryColor || "#0ea5e9",
      teamIds: league.teamIds || [],
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (leagueId) => {
    try {
      await deleteLeague(leagueId);
      toast.success("League deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete league");
    }
  };

  const openNewLeagueDialog = () => {
    setEditingLeague(null);
    setFormData({ name: "", sport: "", logo: "", primaryColor: "#1e3a5f", secondaryColor: "#0ea5e9", teamIds: [] });
    setIsDialogOpen(true);
  };

  const handleTeamToggle = (teamId) => {
    setFormData((prev) => ({
      ...prev,
      teamIds: prev.teamIds.includes(teamId)
        ? prev.teamIds.filter((id) => id !== teamId)
        : [...prev.teamIds, teamId],
    }));
  };

  const handleGenerateFixtures = async () => {
    if (!editingLeague) return;
    if (formData.teamIds.length < 2) {
      toast.error("Please select at least 2 teams to generate fixtures");
      return;
    }

    setGeneratingFixtures(true);
    try {
      await updateLeague(editingLeague.id, formData);
      const count = await generateFixtures(editingLeague.id);
      if (count > 0) {
        toast.success(`Generated ${count} fixtures for ${formData.name}!`, {
          description: `${formData.teamIds.length} teams × round-robin (home & away)`,
        });
      } else {
        toast.error("No fixtures were generated");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate fixtures");
    } finally {
      setGeneratingFixtures(false);
      setIsDialogOpen(false);
    }
  };

  // Filter teams by sport when set; fall back to all teams if the filter
  // would otherwise hide every team (e.g., league has no sport, or a casing
  // mismatch with team.sport). Picker should never silently disappear.
  const sportFiltered = teams.filter(
    (team) => !formData.sport || team.sport === formData.sport
  );
  const availableTeams = sportFiltered.length > 0 ? sportFiltered : teams;
  const showingAllTeams = formData.sport && sportFiltered.length === 0 && teams.length > 0;

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Leagues & Competitions
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage leagues with their logos for thumbnail generation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="gap-2"
            onClick={openBulkDialog}
            disabled={eligibleLeagues.length === 0}
            data-testid="bulk-generate-fixtures-button"
          >
            <Wand2 className="w-4 h-4" />
            Bulk Generate Fixtures
            {eligibleLeagues.length > 0 && (
              <span className="ml-1 text-xs text-muted-foreground">({eligibleLeagues.length})</span>
            )}
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="premium" className="gap-2" onClick={openNewLeagueDialog}>
                <Plus className="w-4 h-4" />
                Add League
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">
                {editingLeague ? "Edit League" : "Add New League"}
              </DialogTitle>
              <DialogDescription>
                {editingLeague
                  ? "Update the league details below"
                  : "Set the league name, sport, logo and brand colors"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Logo Upload & League Info in Row */}
              <div className="flex gap-4 items-start">
                {/* Logo Upload */}
                <div className="flex flex-col items-center flex-shrink-0 gap-1.5">
                  <div
                    className={cn(
                      "w-20 h-20 rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer overflow-hidden",
                      "transition-all duration-200 hover:border-primary/50 hover:bg-primary/5",
                      formData.logo ? "border-primary/30 bg-primary/5" : "border-border"
                    )}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {formData.logo ? (
                      <img
                        src={mediaUrl(formData.logo)}
                        alt="League logo"
                        className="w-full h-full object-contain p-1"
                       loading="lazy" decoding="async"/>
                    ) : (
                      <div className="text-center p-2">
                        <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-1" />
                        <p className="text-[10px] text-muted-foreground">Logo</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="h-6 text-[10px] px-2"
                    onClick={() => setIsMediaPickerOpen(true)}
                    data-testid="league-pick-from-media"
                  >
                    <FolderOpen className="w-3 h-3 mr-1" />
                    Pick from Library
                  </Button>
                </div>

                {/* League Name & Sport */}
                <div className="flex-1 space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="name" className="text-xs">League Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Premier League"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, name: e.target.value }))
                      }
                      className="input-focus h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="sport" className="text-xs">Sport *</Label>
                    <Select
                      value={formData.sport}
                      onValueChange={(value) =>
                        setFormData((prev) => ({ ...prev, sport: value }))
                      }
                    >
                      <SelectTrigger className="input-focus h-9">
                        <SelectValue placeholder="Select sport" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPORTS.map((sport) => (
                          <SelectItem key={sport} value={sport}>
                            {sport}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Colors Row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Primary Color</Label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, primaryColor: e.target.value }))
                      }
                      className="w-9 h-9 rounded cursor-pointer border border-border"
                    />
                    <Input
                      value={formData.primaryColor}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, primaryColor: e.target.value }))
                      }
                      className="flex-1 h-9"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Secondary Color</Label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={formData.secondaryColor}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, secondaryColor: e.target.value }))
                      }
                      className="w-9 h-9 rounded cursor-pointer border border-border"
                    />
                    <Input
                      value={formData.secondaryColor}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, secondaryColor: e.target.value }))
                      }
                      className="flex-1 h-9"
                    />
                  </div>
                </div>
              </div>

              {/* Team Selection — always shown when there are teams */}
              {availableTeams.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Teams in League ({formData.teamIds.length} selected)
                    </Label>
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                      <Checkbox
                        checked={availableTeams.length > 0 && formData.teamIds.length === availableTeams.length}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData((prev) => ({ ...prev, teamIds: availableTeams.map(t => t.id) }));
                          } else {
                            setFormData((prev) => ({ ...prev, teamIds: [] }));
                          }
                        }}
                      />
                      Select All
                    </label>
                  </div>
                  {showingAllTeams && (
                    <p className="text-[10px] text-amber-500/90">
                      No teams match the selected sport — showing all teams instead.
                    </p>
                  )}
                  <div className="max-h-32 overflow-y-auto border border-border rounded-lg p-2 space-y-1 bg-secondary/30">
                    {availableTeams.map((team) => (
                      <label
                        key={team.id}
                        className="flex items-center gap-2 p-1.5 rounded hover:bg-secondary/50 cursor-pointer"
                      >
                        <Checkbox
                          checked={formData.teamIds.includes(team.id)}
                          onCheckedChange={() => handleTeamToggle(team.id)}
                        />
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {team.logo ? (
                            <img src={mediaUrl(team.logo)} alt={team.name} className="w-5 h-5 object-contain"  loading="lazy" decoding="async"/>
                          ) : (
                            <div
                              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                              style={{ backgroundColor: team.primaryColor || '#666' }}
                            >
                              {team.name[0]}
                            </div>
                          )}
                          <span className="text-xs truncate">{team.name}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  {formData.teamIds.length >= 2 && editingLeague && (
                    <p className="text-[10px] text-muted-foreground">
                      Will generate {formData.teamIds.length * (formData.teamIds.length - 1)} fixtures (home & away)
                    </p>
                  )}
                </div>
              )}

              <DialogFooter className="pt-2 flex-col sm:flex-row gap-2">
                {editingLeague && formData.teamIds.length >= 2 && (
                  <Button
                    type="button"
                    variant="premium"
                    size="sm"
                    className="gap-1 w-full sm:w-auto"
                    onClick={handleGenerateFixtures}
                    disabled={submitting || generatingFixtures}
                    data-testid="generate-fixtures-button"
                  >
                    <Calendar className="w-3 h-3" />
                    {generatingFixtures ? "Generating..." : "Generate Fixtures"}
                  </Button>
                )}
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button type="button" variant="outline" size="sm" onClick={() => setIsDialogOpen(false)} disabled={submitting || generatingFixtures}>
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" disabled={submitting || generatingFixtures} data-testid="league-submit-button">
                    {submitting ? "Saving..." : editingLeague ? "Save Changes" : "Add League"}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search leagues by name or sport..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 bg-secondary/50 border-border/50"
        />
      </div>

      {/* Leagues Grid */}
      {filteredLeagues.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredLeagues.map((league) => (
            <Card key={league.id} className="glass-card group">
              <CardContent className="p-4">
                <div
                  className="league-logo w-20 h-20 mx-auto mb-3"
                  style={{
                    background: `linear-gradient(135deg, ${league.primaryColor || '#1e3a5f'}40, ${league.secondaryColor || '#0ea5e9'}40)`,
                  }}
                >
                  {league.logo ? (
                    <img src={mediaUrl(league.logo)} alt={league.name} className="w-full h-full object-contain p-2"  loading="lazy" decoding="async"/>
                  ) : (
                    <Trophy className="w-8 h-8 text-muted-foreground" />
                  )}
                </div>
                <h3 className="text-sm font-semibold text-foreground text-center truncate">
                  {league.name}
                </h3>
                <p className="text-xs text-muted-foreground text-center mt-1">
                  {league.sport}
                </p>
                {league.teamIds && league.teamIds.length > 0 && (
                  <p className="text-[10px] text-primary text-center mt-1">
                    {league.teamIds.length} teams
                  </p>
                )}
                <div className="flex items-center justify-center gap-1 mt-2">
                  <div
                    className="w-4 h-4 rounded-full border border-border"
                    style={{ backgroundColor: league.primaryColor }}
                  />
                  <div
                    className="w-4 h-4 rounded-full border border-border"
                    style={{ backgroundColor: league.secondaryColor }}
                  />
                </div>
                <div className="flex items-center justify-center gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`league-actions-${league.id}`}>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => handleEdit(league)}
                    data-testid={`league-edit-button-${league.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(league.id)}
                    data-testid={`league-delete-button-${league.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="glass-card">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No leagues yet
            </h3>
            <p className="text-muted-foreground mb-4 max-w-sm mx-auto">
              Start by adding leagues with their logos. These will be displayed in the center of thumbnails.
            </p>
            <Button variant="premium" className="gap-2" onClick={openNewLeagueDialog}>
              <Plus className="w-4 h-4" />
              Add Your First League
            </Button>
          </CardContent>
        </Card>
      )}

      <MediaPicker
        open={isMediaPickerOpen}
        onOpenChange={setIsMediaPickerOpen}
        onSelect={handleMediaPick}
        title="Pick a league logo"
      />

      {/* Bulk Generate Fixtures Dialog */}
      <Dialog open={isBulkOpen} onOpenChange={(o) => !bulkRunning && setIsBulkOpen(o)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-primary" />
              Bulk Generate Fixtures
            </DialogTitle>
            <DialogDescription>
              Pick the leagues and we'll generate round-robin fixtures (home & away) for each one.
              Existing auto-generated fixtures will be replaced; manually edited matches are kept.
            </DialogDescription>
          </DialogHeader>

          {eligibleLeagues.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No leagues with 2+ teams yet. Add teams to a league first.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={toggleBulkAll}
                  disabled={bulkRunning}
                  className="text-primary hover:underline disabled:opacity-50"
                  data-testid="bulk-toggle-all"
                >
                  {bulkSelectedIds.length === eligibleLeagues.length ? "Deselect all" : "Select all"}
                </button>
                <span className="text-muted-foreground">
                  {bulkSelectedIds.length} of {eligibleLeagues.length} selected
                </span>
              </div>

              <div className="max-h-[280px] overflow-y-auto rounded-md border border-border divide-y divide-border">
                {eligibleLeagues.map((league) => {
                  const result = bulkResults.find((r) => r.leagueId === league.id);
                  const teamCount = league.teamIds?.length || 0;
                  const fixtureCount = teamCount * (teamCount - 1);
                  const isCurrent = bulkRunning && bulkProgress.current === league.name;
                  return (
                    <label
                      key={league.id}
                      className={cn(
                        "flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary/40",
                        bulkRunning && "cursor-default",
                        isCurrent && "bg-primary/5"
                      )}
                      data-testid={`bulk-league-row-${league.id}`}
                    >
                      <Checkbox
                        checked={bulkSelectedIds.includes(league.id)}
                        onCheckedChange={() => toggleBulkLeague(league.id)}
                        disabled={bulkRunning}
                      />
                      <div
                        className="w-8 h-8 rounded shrink-0 flex items-center justify-center overflow-hidden"
                        style={{ background: `linear-gradient(135deg, ${league.primaryColor}40, ${league.secondaryColor || league.primaryColor}40)` }}
                      >
                        {league.logo ? (
                          <img src={mediaUrl(league.logo)} alt="" className="w-full h-full object-contain p-0.5" />
                        ) : (
                          <Trophy className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{league.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {teamCount} teams · {fixtureCount} fixtures
                        </p>
                      </div>
                      {isCurrent && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
                      {result?.ok && (
                        <span className="flex items-center gap-1 text-xs text-emerald-500">
                          <CheckCircle2 className="w-4 h-4" /> {result.count}
                        </span>
                      )}
                      {result && !result.ok && (
                        <span
                          className="flex items-center gap-1 text-xs text-destructive"
                          title={result.error}
                        >
                          <XCircle className="w-4 h-4" /> Failed
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>

              {bulkRunning && (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span className="truncate">{bulkProgress.current || "Starting..."}</span>
                    <span>{bulkProgress.done} / {bulkProgress.total}</span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsBulkOpen(false)}
              disabled={bulkRunning}
            >
              {bulkResults.length > 0 ? "Close" : "Cancel"}
            </Button>
            <Button
              type="button"
              variant="premium"
              onClick={runBulkGenerate}
              disabled={bulkRunning || bulkSelectedIds.length === 0 || eligibleLeagues.length === 0}
              data-testid="bulk-generate-run-button"
            >
              {bulkRunning ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Wand2 className="w-4 h-4 mr-2" /> Generate for {bulkSelectedIds.length} league{bulkSelectedIds.length === 1 ? "" : "s"}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
